/**
 * Admin Ticket Detail Page
 *
 * Admin view for ticket details with additional controls:
 * - Assign/reassign ticket
 * - Change priority
 * - Add internal notes
 * - Change ticket status
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, AlertCircle, Edit2, Lock } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Select } from '../../components/common/Select';
import {
  TicketStatusBadge,
  TicketPriorityBadge,
  TicketTypeBadge,
} from '../../components/support';
import { TicketMessageList } from '../../components/support/TicketMessageList';
import { TicketMessageForm } from '../../components/support/TicketMessageForm';
import type {
  SupportTicket,
  TicketMessage,
  TicketStatus,
  TicketPriority,
} from '../../types/support';

export const AdminTicketDetailPage: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(false);
  const [editingPriority, setEditingPriority] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadTicketDetails();
    }
  }, [ticketId]);

  const loadTicketDetails = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API calls when Phase 1E is implemented
      // const ticketResponse = await mockApi.getTicketById(ticketId);
      // const messagesResponse = await mockApi.getTicketMessages(ticketId);

      // Mock data for now
      setTicket(null);
      setMessages([]);
    } catch (error) {
      console.error('Failed to load ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticketId) return;

    setSendingMessage(true);
    try {
      // TODO: Replace with actual API call
      // await mockApi.addTicketMessage(ticketId, newMessage, isInternal: isInternalNote);

      setNewMessage('');
      setIsInternalNote(false);
      await loadTicketDetails();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAssignTicket = async (_assigneeId: string) => {
    if (!ticketId) return;

    try {
      // TODO: Replace with actual API call
      // await mockApi.assignTicket(ticketId, assigneeId);

      setEditingAssignment(false);
      await loadTicketDetails();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
    }
  };

  const handleChangePriority = async (_newPriority: TicketPriority) => {
    if (!ticketId) return;

    try {
      // TODO: Replace with actual API call
      // await mockApi.updateTicket(ticketId, { priority: newPriority });

      setEditingPriority(false);
      await loadTicketDetails();
    } catch (error) {
      console.error('Failed to change priority:', error);
    }
  };

  const handleChangeStatus = async (_newStatus: TicketStatus) => {
    if (!ticketId) return;

    try {
      // TODO: Replace with actual API call
      // await mockApi.updateTicket(ticketId, { status: newStatus });

      await loadTicketDetails();
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle="Loading..."
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Admin', href: '/admin/dashboard' },
          { label: 'Support', href: '/admin/support' },
          { label: 'Tickets', href: '/admin/support/tickets' },
          { label: 'Loading...' },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-neutral-200 rounded-lg"></div>
          <div className="h-96 bg-neutral-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  if (!ticket) {
    return (
      <PageLayout
        pageTitle="Ticket Not Found"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Admin', href: '/admin/dashboard' },
          { label: 'Support', href: '/admin/support' },
          { label: 'Tickets', href: '/admin/support/tickets' },
          { label: 'Not Found' },
        ]}
      >
        <Card>
          <CardBody className="text-center py-12">
            <p className="text-neutral-600 mb-4">The ticket you're looking for doesn't exist.</p>
            <Button variant="primary" onClick={() => navigate('/admin/support/tickets')}>
              Back to Ticket Queue
            </Button>
          </CardBody>
        </Card>
      </PageLayout>
    );
  }

  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    // TODO: Load support staff from API
    { value: 'staff-001', label: 'John Smith (Consultant)' },
    { value: 'staff-002', label: 'Sarah Johnson (Support Staff)' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'waiting_customer', label: 'Waiting for Customer' },
    { value: 'waiting_internal', label: 'Waiting for Internal' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  return (
    <PageLayout
      pageTitle={`Ticket ${ticket.ticketNumber}`}
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Admin', href: '/admin/dashboard' },
        { label: 'Support', href: '/admin/support' },
        { label: 'Tickets', href: '/admin/support/tickets' },
        { label: ticket.ticketNumber },
      ]}
      actions={
        <Button
          variant="outline"
          icon={<ArrowLeft className="w-4 h-4" />}
          onClick={() => navigate('/admin/support/tickets')}
        >
          Back to Queue
        </Button>
      }
    >
      {/* Ticket Header */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-mono text-neutral-600">{ticket.ticketNumber}</span>
                <TicketStatusBadge status={ticket.status} />
                <TicketPriorityBadge priority={ticket.priority} />
                <TicketTypeBadge type={ticket.type} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{ticket.subject}</h2>
              <p className="text-sm text-neutral-700 mb-2">{ticket.description}</p>
              <p className="text-sm text-neutral-600">
                <span className="font-medium">Company:</span> {ticket.companyName}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-neutral-200 text-sm">
            <div>
              <span className="text-neutral-600">Created:</span>
              <p className="font-medium">{formatDate(ticket.createdAt)}</p>
            </div>
            <div>
              <span className="text-neutral-600">Last Updated:</span>
              <p className="font-medium">{formatDate(ticket.updatedAt)}</p>
            </div>
            {ticket.slaDeadline && (
              <div>
                <span className="text-neutral-600">SLA Deadline:</span>
                <p className="font-medium">{formatDate(ticket.slaDeadline)}</p>
              </div>
            )}
            {ticket.resolvedAt && (
              <div>
                <span className="text-neutral-600">Resolved:</span>
                <p className="font-medium">{formatDate(ticket.resolvedAt)}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Admin Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Assignment */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-neutral-600" />
                <span className="text-sm font-medium">Assigned To</span>
              </div>
              <button
                onClick={() => setEditingAssignment(!editingAssignment)}
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <Edit2 className="w-4 h-4 text-neutral-600" />
              </button>
            </div>
            {editingAssignment ? (
              <Select
                value={ticket.assignedTo || ''}
                onChange={(event) => handleAssignTicket((event.target as HTMLSelectElement).value)}
              >
                {assigneeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="text-sm">{ticket.assignedToName || 'Unassigned'}</p>
            )}
          </CardBody>
        </Card>

        {/* Priority */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-neutral-600" />
                <span className="text-sm font-medium">Priority</span>
              </div>
              <button
                onClick={() => setEditingPriority(!editingPriority)}
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <Edit2 className="w-4 h-4 text-neutral-600" />
              </button>
            </div>
            {editingPriority ? (
              <Select
                value={ticket.priority}
                onChange={(event) => handleChangePriority((event.target as HTMLSelectElement).value as unknown as TicketPriority)}
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : (
              <TicketPriorityBadge priority={ticket.priority} />
            )}
          </CardBody>
        </Card>

        {/* Status */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-neutral-600" />
              <span className="text-sm font-medium">Status</span>
            </div>
            <Select value={ticket.status} onChange={(event) => handleChangeStatus((event.target as HTMLSelectElement).value as unknown as TicketStatus)}>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </CardBody>
        </Card>
      </div>

      {/* Conversation */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">
            Conversation
            {messages.length > 0 && (
              <span className="ml-2 text-sm font-normal text-neutral-600">
                ({messages.length} {messages.length === 1 ? 'message' : 'messages'})
              </span>
            )}
          </h3>
        </CardHeader>
        <CardBody>
          <TicketMessageList messages={messages} showInternal />

          {/* Message Form */}
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="internal-note"
                checked={isInternalNote}
                onChange={(e) => setIsInternalNote(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
              />
              <label htmlFor="internal-note" className="text-sm text-neutral-700 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Internal note (not visible to customer)
              </label>
            </div>

            <TicketMessageForm
              value={newMessage}
              onChange={setNewMessage}
              onSubmit={handleSendMessage}
              loading={sendingMessage}
            />
          </div>
        </CardBody>
      </Card>
    </PageLayout>
  );
};
